import { Link } from "react-router-dom";
import { FileText, Scale, AlertTriangle, CreditCard, Ban, Gavel } from "lucide-react";

const TermsOfService = () => {
  const lastUpdated = "January 21, 2026";
  const effectiveDate = "January 21, 2026";
  return <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black backdrop-blur-md border-b border-white/10">
        
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-10 h-10 text-purple-500" />
            <h1 className="text-4xl md:text-5xl font-bold">Terms of Service</h1>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-gray-400">
            <span>Last Updated: {lastUpdated}</span>
            <span>•</span>
            <span>Effective Date: {effectiveDate}</span>
          </div>
        </div>

        {/* Agreement Notice */}
        <div className="mb-10 p-6 bg-purple-500/10 border border-purple-500/20 rounded-xl">
          <p className="text-purple-300">
            <strong>IMPORTANT:</strong> By accessing or using Metro City Groove's services, you agree to be bound by these 
            Terms of Service. If you do not agree to these terms, you may not use our Services.
          </p>
        </div>

        {/* Table of Contents */}
        <nav className="mb-12 p-6 bg-white/5 rounded-xl border border-white/10">
          <h2 className="text-xl font-semibold mb-4">Contents</h2>
          <ul className="space-y-2 text-gray-400 grid grid-cols-1 md:grid-cols-2 gap-2">
            {["Acceptance of Terms", "Description of Services", "User Accounts", "User Types and Roles", "User Conduct", "Intellectual Property", "Payments and Fees", "Booking and Transactions", "Content and Media", "Disclaimers", "Limitation of Liability", "Indemnification", "Dispute Resolution", "Termination", "Governing Law", "Changes to Terms", "General Provisions", "Contact Information"].map((item, index) => <li key={index}>
                <a href={`#tos-section-${index + 1}`} className="hover:text-white transition-colors">
                  {index + 1}. {item}
                </a>
              </li>)}
          </ul>
        </nav>

        {/* Section 1 */}
        <section id="tos-section-1" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-semibold">1. Acceptance of Terms</h2>
          </div>
          
          <div className="space-y-4 text-gray-300">
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you and Metro City 
              Trading (Pty) Ltd ("Metro City Groove", "Company", "we", "us", or "our") governing your access to 
              and use of the Metro City Groove platform, including any associated websites, mobile applications, 
              and services (collectively, the "Services").
            </p>
            <p>
              By creating an account, accessing, or using our Services, you acknowledge that you have read, 
              understood, and agree to be bound by these Terms, along with our <Link to="/privacy" className="text-purple-400 hover:underline">Privacy Policy</Link>, 
              <Link to="/cookies" className="text-purple-400 hover:underline"> Cookie Policy</Link>, and any additional terms 
              applicable to specific features.
            </p>
            <p className="text-gray-400">
              You must be at least 18 years old to use our Services. By using our Services, you represent and 
              warrant that you meet this age requirement.
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section id="tos-section-2" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">2. Description of Services</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              Metro City Groove is a digital platform connecting entertainment industry professionals, venues, 
              and audiences across Africa. Our Services include but are not limited to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>A marketplace connecting creators (DJs, artists, performers) with venues and event organizers</li>
              <li>Event discovery and ticket purchasing for entertainment experiences</li>
              <li>Profile creation and portfolio management for entertainment professionals</li>
              <li>Booking management and scheduling tools</li>
              <li>Payment processing for bookings and ticket sales</li>
              <li>Communication tools between platform users</li>
              <li>Content sharing and social networking features</li>
              <li>Agency management services for creator representation</li>
            </ul>
          </div>
        </section>

        {/* Section 3 */}
        <section id="tos-section-3" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">3. User Accounts</h2>
          
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.1 Account Registration</h3>
              <p className="text-gray-400">
                To access certain features, you must create an account. You agree to provide accurate, current, 
                and complete information during registration and to update this information as necessary.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.2 Account Security</h3>
              <p className="text-gray-400">
                You are responsible for maintaining the confidentiality of your account credentials and for all 
                activities under your account. You must immediately notify us of any unauthorized use of your account.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.3 Account Verification</h3>
              <p className="text-gray-400">
                We may require verification of your identity or credentials. Creators and venues may need to 
                complete additional verification to access certain features. Providing false information may 
                result in account suspension or termination.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">3.4 One Account Per Person</h3>
              <p className="text-gray-400">
                Each individual may maintain only one personal account. Venues and agencies may have separate 
                business accounts with proper authorization.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="tos-section-4" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">4. User Types and Roles</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>Our platform serves three primary user types:</p>
            
            <div className="space-y-4">
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">Creators</h4>
                <p className="text-gray-400">DJs, artists, performers, and entertainment professionals who offer their services through the platform.</p>
              </div>
              
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">Venues</h4>
                <p className="text-gray-400">Clubs, bars, event spaces, and organizers who book creators and host events.</p>
              </div>
              
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <h4 className="font-semibold text-white mb-2">Groovers</h4>
                <p className="text-gray-400">Fans and attendees who discover events, follow creators, and purchase tickets.</p>
              </div>
            </div>
            
            <p className="text-gray-400">
              Each user type has specific features, responsibilities, and terms applicable to their use of the platform.
            </p>
          </div>
        </section>

        {/* Section 5 */}
        <section id="tos-section-5" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Ban className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-semibold">5. User Conduct</h2>
          </div>
          
          <div className="space-y-4 text-gray-300">
            <p>You agree NOT to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Violate any applicable laws, regulations, or third-party rights</li>
              <li>Post false, misleading, defamatory, or fraudulent content</li>
              <li>Harass, threaten, or intimidate other users</li>
              <li>Engage in discriminatory behavior based on race, gender, religion, nationality, or other protected characteristics</li>
              <li>Attempt to circumvent platform fees or payment systems</li>
              <li>Use automated systems or bots without authorization</li>
              <li>Interfere with the proper functioning of the platform</li>
              <li>Upload malware, viruses, or harmful code</li>
              <li>Collect user data without consent</li>
              <li>Impersonate others or create fake accounts</li>
              <li>Engage in ticket scalping or unauthorized resale</li>
              <li>Promote violence, illegal activities, or harmful content</li>
            </ul>
            <p className="text-gray-400">
              Violation of these rules may result in content removal, account suspension, or permanent ban from the platform.
            </p>
          </div>
        </section>

        {/* Section 6 */}
        <section id="tos-section-6" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">6. Intellectual Property</h2>
          
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">6.1 Platform Content</h3>
              <p className="text-gray-400">
                The Metro City Groove name, logo, and all related marks, graphics, software, and content are 
                owned by or licensed to us. You may not use our intellectual property without prior written consent.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">6.2 Your Content</h3>
              <p className="text-gray-400">
                You retain ownership of content you create and upload. By posting content on our platform, you 
                grant us a non-exclusive, worldwide, royalty-free license to use, display, reproduce, and 
                distribute your content in connection with operating and promoting our Services.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">6.3 Copyright Infringement</h3>
              <p className="text-gray-400">
                We respect intellectual property rights. If you believe your copyrighted work has been infringed, 
                please contact us with a detailed notice including identification of the work, location on our 
                platform, and your contact information.
              </p>
            </div>
          </div>
        </section>

        {/* Section 7 */}
        <section id="tos-section-7" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-semibold">7. Payments and Fees</h2>
          </div>
          
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">7.1 Platform Fees</h3>
              <p className="text-gray-400">
                We charge fees for certain transactions including bookings, ticket sales, and premium features. 
                Current fee structures are displayed during transactions and on our pricing pages.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">7.2 Payment Processing</h3>
              <p className="text-gray-400">
                Payments are processed through secure third-party payment providers. By using our payment features, 
                you agree to the terms of these payment processors.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">7.3 Payouts</h3>
              <p className="text-gray-400">
                Creators and venues receive payouts according to our payout schedule after applicable fees are 
                deducted. Payout timing and methods are detailed in your account settings.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-medium text-white mb-2">7.4 Taxes</h3>
              <p className="text-gray-400">
                You are responsible for determining and paying applicable taxes on your earnings. We may provide 
                tax documentation as required by law.
              </p>
            </div>
          </div>
        </section>

        {/* Section 8 */}
        <section id="tos-section-8" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">8. Booking and Transactions</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              When you make or accept a booking through our platform:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Bookings are binding agreements between creators and venues</li>
              <li>Cancellation policies apply as specified in individual bookings</li>
              <li>We may hold funds in escrow until services are completed</li>
              <li>Disputes should first be resolved between parties; we may mediate if needed</li>
              <li>Failure to fulfill bookings may result in penalties or account restrictions</li>
            </ul>
          </div>
        </section>

        {/* Section 9 */}
        <section id="tos-section-9" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">9. Content and Media</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>Content you upload must comply with our Community Guidelines:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>You must own or have rights to all content you post</li>
              <li>Content must not contain illegal, harmful, or offensive material</li>
              <li>Music content must have proper licensing</li>
              <li>We may remove content that violates these Terms without notice</li>
              <li>You are solely responsible for your content and its consequences</li>
            </ul>
          </div>
        </section>

        {/* Section 10 */}
        <section id="tos-section-10" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-yellow-400" />
            <h2 className="text-2xl font-semibold">10. Disclaimers</h2>
          </div>
          
          <div className="space-y-4 text-gray-300 p-6 bg-yellow-500/5 border border-yellow-500/20 rounded-xl">
            <p className="uppercase font-semibold text-yellow-300">
              THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.
            </p>
            <p className="text-gray-400">
              We do not warrant that the Services will be uninterrupted, error-free, or completely secure. 
              We are not responsible for the conduct of users, the quality of services provided by creators 
              or venues, or the safety of events.
            </p>
            <p className="text-gray-400">
              We disclaim all warranties, express or implied, including merchantability, fitness for a particular 
              purpose, and non-infringement to the maximum extent permitted by law.
            </p>
          </div>
        </section>

        {/* Section 11 */}
        <section id="tos-section-11" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">11. Limitation of Liability</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>We are not liable for indirect, incidental, special, consequential, or punitive damages</li>
              <li>Our total liability shall not exceed the fees you paid us in the 12 months preceding the claim</li>
              <li>We are not liable for third-party actions, user-generated content, or events beyond our control</li>
              <li>These limitations apply regardless of the theory of liability</li>
            </ul>
          </div>
        </section>

        {/* Section 12 */}
        <section id="tos-section-12" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">12. Indemnification</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              You agree to indemnify, defend, and hold harmless Metro City Groove, its officers, directors, 
              employees, agents, and affiliates from any claims, damages, losses, liabilities, and expenses 
              (including legal fees) arising from:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Your use of the Services</li>
              <li>Your violation of these Terms</li>
              <li>Your content or conduct on the platform</li>
              <li>Your violation of any third-party rights</li>
            </ul>
          </div>
        </section>

        {/* Section 13 */}
        <section id="tos-section-13" className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Gavel className="w-6 h-6 text-purple-400" />
            <h2 className="text-2xl font-semibold">13. Dispute Resolution</h2>
          </div>
          
          <div className="space-y-4 text-gray-300">
            <p>
              In the event of a dispute:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>We encourage parties to first attempt informal resolution by contacting support</li>
              <li>If unresolved, disputes will be submitted to binding arbitration in South Africa</li>
              <li>Class action waivers apply to the extent permitted by law</li>
              <li>Small claims court remains available for eligible disputes</li>
            </ul>
          </div>
        </section>

        {/* Section 14 */}
        <section id="tos-section-14" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">14. Termination</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              We may suspend or terminate your account at any time for violations of these Terms or for any 
              other reason with or without notice. You may also delete your account at any time through your 
              account settings.
            </p>
            <p className="text-gray-400">
              Upon termination, your right to use the Services ceases immediately. Provisions that by their 
              nature should survive termination will remain in effect.
            </p>
          </div>
        </section>

        {/* Section 15 */}
        <section id="tos-section-15" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">15. Governing Law</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              These Terms are governed by the laws of the Republic of South Africa, without regard to conflict 
              of law principles. Any legal proceedings must be brought in the courts of South Africa.
            </p>
          </div>
        </section>

        {/* Section 16 */}
        <section id="tos-section-16" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">16. Changes to Terms</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of material changes 
              through the platform or by email. Your continued use of the Services after changes take effect 
              constitutes acceptance of the new Terms.
            </p>
          </div>
        </section>

        {/* Section 17 */}
        <section id="tos-section-17" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">17. General Provisions</h2>
          
          <div className="space-y-4 text-gray-300">
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and Metro City Groove.</li>
              <li><strong>Severability:</strong> If any provision is found unenforceable, other provisions remain in effect.</li>
              <li><strong>Waiver:</strong> Our failure to enforce a provision does not waive our right to do so later.</li>
              <li><strong>Assignment:</strong> You may not assign your rights without our consent; we may assign freely.</li>
              <li><strong>Force Majeure:</strong> We are not liable for delays due to circumstances beyond our control.</li>
            </ul>
          </div>
        </section>

        {/* Section 18 */}
        <section id="tos-section-18" className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">18. Contact Information</h2>
          
          <div className="space-y-4 text-gray-300">
            <p>For questions about these Terms:</p>
            <div className="p-6 bg-white/5 rounded-xl border border-white/10 space-y-3">
              <p><strong>Metro City Trading (Pty) Ltd</strong></p>
              <p className="text-gray-400">Legal Department</p>
              <p className="text-gray-400">Email: <a href="mailto:legal@metrocitygroove.online" className="text-purple-400 hover:underline">legal@metrocitygroove.online</a></p>
              <p className="text-gray-400">General: <a href="mailto:info@metrocitygroove.online" className="text-purple-400 hover:underline">info@metrocitygroove.online</a></p>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="mt-12 p-6 bg-white/5 rounded-xl border border-white/10">
          <h3 className="text-lg font-semibold mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="text-purple-400 hover:underline">Privacy Policy</Link>
            <Link to="/cookies" className="text-purple-400 hover:underline">Cookie Policy</Link>
            <Link to="/gdpr" className="text-purple-400 hover:underline">GDPR Compliance</Link>
          </div>
        </section>
      </main>
    </div>;
};
export default TermsOfService;